/**
 * LeadsManager — Simplified emotional lead dashboard
 * 3 statuses: Nowy, W trakcie, Zamkniety
 * 3 actions: Odpisz, Zadzwon, Oznacz jako sprzedane
 * Campaign connection + Feedback loop
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import StudioShell from '@/components/studio/StudioShell';
import { Button } from '@/components/ui/button';
import {
  Flame, Mail, Phone, CheckCircle2,
  Inbox, Rocket, Eye, MessageCircle, PhoneCall, Send,
  ChevronDown, ChevronUp, ChevronRight, X, Sparkles, Copy, AlertTriangle, Trash2, Loader2,
  Plus, SlidersHorizontal, Lightbulb, User, PawPrint,
  Home, Baby, Dog, HelpCircle, ArrowRight, Calendar
} from 'lucide-react';
import { API } from '@/utils/api';

/* ── Status config ─────────────────────────────────── */
const STATUS_MAP = {
  new:         { labelKey: 'leads.new', rowLabel: 'Nowe',        color: '#F47A3B', bg: '#FFF3EC', dot: '#F47A3B' },
  in_progress: { labelKey: 'leads.inProgress', rowLabel: 'W trakcie', color: '#3B82F6', bg: '#EFF6FF', dot: '#3B82F6' },
  reserved:    { label: 'Rezerwacja', rowLabel: 'Rezerwacja', color: '#8B5CF6', bg: '#F5F3FF', dot: '#8B5CF6' },
  closed:      { labelKey: 'leads.won', rowLabel: 'Zakończone',  color: '#10B981', bg: '#F0FDF4', dot: '#10B981' },
};

// Pipeline 4-stanowy (zgodnie z modelem Zapytań V2): Nowe → W kontakcie → Rezerwacja → Zamknięte
const PIPELINE_STATUSES = [
  { id: 'new', label: 'Nowe' },
  { id: 'in_progress', label: 'W kontakcie' },
  { id: 'reserved', label: 'Rezerwacja' },
  { id: 'closed', label: 'Zamknięte' },
];

const FILTER_TABS = [
  { id: 'all', label: 'Wszystkie' },
  { id: 'new', label: 'Nowe' },
  { id: 'in_progress', label: 'W kontakcie' },
  { id: 'reserved', label: 'Rezerwacje' },
  { id: 'closed', label: 'Zamknięte' },
];

// Filtr „Źródło" — z której strony przyszło zapytanie (zgodnie z makietą).
const SOURCE_FILTER_OPTIONS = [
  { id: 'all', label: 'Wszystkie' },
  { id: 'kennel', label: 'Strona hodowli' },
  { id: 'litter', label: 'Miot' },
  { id: 'stud', label: 'Reproduktor' },
  { id: 'katalog', label: 'Katalog' },
];

// Filtr „Typ" — czego dotyczy zapytanie.
const TYPE_FILTER_OPTIONS = [
  { id: 'all', label: 'Wszystkie' },
  { id: 'puppies', label: 'Szczenięta' },
  { id: 'mating', label: 'Krycie' },
  { id: 'kennel', label: 'Hodowla' },
  { id: 'other', label: 'Inne' },
];

/* ── Source labels ──────────────────────────────────── */
const SOURCE_LABELS = {
  booster: 'Promocja',
  form: 'Strona',
  direct: 'Bezpośredni',
  ai_network: 'Sieć',
  ai_search: 'Wyszukiwanie',
  google_organic: 'Google',
  social: 'Social',
  facebook_comment: 'Komentarz FB',
  direct: 'Bezpośrednie',
  manual: 'Dodane ręcznie',
};

/* ── Entity labels (prefix wiersza leada) ───────────── */
const ENTITY_LABELS = {
  litter: 'Miot: ',
  stud: 'Reproduktor: ',
  dog: 'Pies: ',
  kennel: 'Hodowla: ',
  service: '',
  manual: '',
};

/* ── Classification labels ──────────────────────────── */
const CLASS_CONFIG = {
  lead: { label: 'Lead', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  question: { label: 'Pytanie', bg: 'bg-blue-100', text: 'text-blue-700' },
  warm: { label: 'Ciepły', bg: 'bg-amber-100', text: 'text-amber-700' },
  negative: { label: 'Negatywny', bg: 'bg-red-100', text: 'text-red-700' },
  spam: { label: 'Spam', bg: 'bg-zinc-100', text: 'text-zinc-500' },
};

/* ── Helpers ────────────────────────────────────────── */
function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

function normalizeStatus(s) {
  if (s === 'new' || s === 'to_reply') return 'new';
  if (s === 'in_progress' || s === 'talking' || s === 'contacted') return 'in_progress';
  if (s === 'reserved' || s === 'reservation') return 'reserved';
  if (s === 'closed' || s === 'sold' || s === 'done' || s === 'archived') return 'closed';
  return 'new';
}

/* ── Źródło / Typ zapytania (filtry zgodne z makietą) ── */
function leadZrodlo(lead) {
  const et = lead.entity_type;
  if (et === 'kennel') return 'kennel';
  if (et === 'litter') return 'litter';
  if (et === 'stud' || et === 'dog') return 'stud';
  const src = lead.source_type || lead.source || '';
  if (['ai_network', 'ai_search', 'google_organic', 'catalog', 'seo_programmatic'].includes(src)) return 'katalog';
  return 'other';
}

function leadTyp(lead) {
  const et = lead.entity_type;
  if (et === 'litter') return 'puppies';
  if (et === 'stud' || et === 'dog') return 'mating';
  if (et === 'kennel') return 'kennel';
  return 'other';
}

/* ── Źródło → etykieta wiersza („Strona miotu" itp.) ── */
function leadSourceLabel(lead) {
  switch (leadZrodlo(lead)) {
    case 'kennel': return 'Strona hodowli';
    case 'litter': return 'Strona miotu';
    case 'stud': return 'Strona reproduktora';
    case 'katalog': return 'Katalog APISAI';
    default: return 'Bezpośrednie';
  }
}

function waitingMinutes(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
}

/* ── Phone normalization (PL focus) ─────────────────── */
function normalizePLPhone(phone) {
  if (!phone) return null;
  let cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  // Remove leading +
  if (cleaned.startsWith('+')) cleaned = cleaned.slice(1);
  // If 9 digits starting with typical PL mobile prefixes
  if (/^[4-9]\d{8}$/.test(cleaned)) cleaned = '48' + cleaned;
  // Validate: must be at least 10 digits
  if (!/^\d{10,15}$/.test(cleaned)) return null;
  return cleaned;
}

function buildWhatsAppLink(phone, message) {
  const normalized = normalizePLPhone(phone);
  if (!normalized) return null;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
}

/* ── Response time helpers ──────────────────────────── */
function formatResponseTime(seconds) {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d`;
}

function responseTimeBadge(lead) {
  const status = normalizeStatus(lead.status);
  if (status === 'new' && !lead.first_contact_at) {
    // No response yet — show waiting time
    const mins = waitingMinutes(lead.created_at);
    if (mins < 15) return { color: '#10B981', bg: '#F0FDF4', text: `Czeka ${mins} min` };
    if (mins < 60) return { color: '#F59E0B', bg: '#FEF3C7', text: `Czeka ${mins} min` };
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return { color: '#DC2626', bg: '#FEE2E2', text: `PILNE · Czeka ${hrs}h`, urgent: true };
    const days = Math.floor(hrs / 24);
    return { color: '#DC2626', bg: '#FEE2E2', text: `PILNE · Czeka ${days} ${days === 1 ? 'dzień' : 'dni'}`, urgent: true };
  }
  if (lead.response_time_seconds > 0) {
    const formatted = formatResponseTime(lead.response_time_seconds);
    const secs = lead.response_time_seconds;
    if (secs < 900) return { color: '#10B981', bg: '#F0FDF4', text: `Odpowiedź w ${formatted}` };
    if (secs < 3600) return { color: '#F59E0B', bg: '#FEF3C7', text: `Odpowiedź w ${formatted}` };
    return { color: '#F47A3B', bg: '#FFF3EC', text: `Odpowiedź w ${formatted}` };
  }
  return null;
}

const QUICK_REPLY_TEXT = 'Dzień dobry, dziękuję za wiadomość. Chętnie pomogę i odpowiem na pytania.';

/* ── Lead Card ──────────────────────────────────────── */
function LeadCard({ lead, isExpanded, onToggle, onStatusChange, onOpenResponseHub, onDelete, onMarkAsClient, onSendSuggestedDM }) {
  const { t } = useTranslation();
  const [copyLabel, setCopyLabel] = useState('Kopiuj wiadomość');
  const [showContactedHint, setShowContactedHint] = useState(false);
  const [showDemoCoach, setShowDemoCoach] = useState(() => {
    if (!lead.is_demo) return false;
    try { return localStorage.getItem('apisai_demo_coach_dismissed') !== '1'; } catch { return true; }
  });
  const dismissDemoCoach = () => {
    setShowDemoCoach(false);
    try { localStorage.setItem('apisai_demo_coach_dismissed', '1'); } catch {}
  };
  const status = STATUS_MAP[normalizeStatus(lead.status)] || STATUS_MAP.new;
  const isNew = normalizeStatus(lead.status) === 'new';
  const rtBadge = responseTimeBadge(lead);
  const whatsappLink = buildWhatsAppLink(lead.phone, `Dzień dobry${lead.name ? ` ${lead.name.split(' ')[0]}` : ''}, dziękuję za kontakt${lead.entity_name ? ` w sprawie ${lead.entity_name}` : ''}. Chętnie podam szczegóły.`);

  const logEvent = async (eventType) => {
    try {
      await axios.post(`${API}/leads/${lead.id}/event`, {
        event_type: eventType,
        metadata: { source: 'lead_card' }
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    } catch (e) { /* silent */ }
  };

  const handleContactAction = (eventType) => {
    logEvent(eventType);
    if (isNew) {
      onStatusChange(lead.id, 'in_progress');
      setShowContactedHint(true);
      setTimeout(() => setShowContactedHint(false), 5000);
    }
  };

  const source = SOURCE_LABELS[lead.source_type] || 'Bezpośrednie';

  return (
    <div
      className={`transition-colors ${isExpanded ? 'bg-[#FAFBFC]' : 'hover:bg-[#FAFBFC]/70'}`}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Wiersz podsumowania */}
      <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 cursor-pointer" onClick={onToggle}>
        {/* Avatar */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
          style={{ background: isNew ? '#F47A3B' : '#CBD5E1' }}
        >
          {(lead.name || '?')[0].toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[15px] font-bold text-zinc-900 truncate">
              {lead.name || 'Nieznany'}
            </span>
            {lead.is_demo && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 bg-emerald-100 text-emerald-700" data-testid={`demo-pill-${lead.id}`}>
                DEMO
              </span>
            )}
            <span
              className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ color: status.color, background: status.bg }}
              data-testid={`status-pill-${lead.id}`}
            >
              {status.rowLabel}
            </span>
            {rtBadge && rtBadge.urgent && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ color: rtBadge.color, background: rtBadge.bg }}
                data-testid={`urgent-badge-${lead.id}`}
              >
                <AlertTriangle className="w-3 h-3" /> {rtBadge.text}
              </span>
            )}
            {lead.classification && CLASS_CONFIG[lead.classification] && (
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${CLASS_CONFIG[lead.classification].bg} ${CLASS_CONFIG[lead.classification].text}`}
                data-testid={`class-badge-${lead.id}`}
              >
                {CLASS_CONFIG[lead.classification].label}
              </span>
            )}
          </div>
          <p className="text-[13px] text-zinc-500 line-clamp-1 mb-1.5">
            &ldquo;{lead.source_comment_text || lead.message || 'Brak wiadomości'}&rdquo;
          </p>
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-zinc-400">{formatDateTime(lead.created_at)}</span>
            <span className="text-zinc-300">·</span>
            <span className="font-medium text-zinc-400 bg-[#F3F4F6] px-2 py-0.5 rounded-full">
              {SOURCE_LABELS[lead.source] || source}
            </span>
            {lead.entity_name && (
              <span className="inline-flex items-center gap-1 text-zinc-400">
                <PawPrint className="w-3 h-3" /> {ENTITY_LABELS[lead.entity_type] || ''}{lead.entity_name}
              </span>
            )}
          </div>
        </div>

        {/* Akcje po prawej */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleContactAction('whatsapp_clicked')}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#25D366]/10 text-[#1DA851] hover:bg-[#25D366]/20 transition-colors"
              data-testid={`quick-whatsapp-${lead.id}`}
              title="WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          )}
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              onClick={() => handleContactAction('call_clicked')}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              data-testid={`quick-call-${lead.id}`}
              title="Zadzwoń"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
          {lead.email && (
            <a
              href={`mailto:${lead.email}?subject=Re: ${lead.entity_name || 'Zapytanie'}`}
              onClick={() => handleContactAction('email_clicked')}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#F47A3B]/10 text-[#F47A3B] hover:bg-[#F47A3B]/20 transition-colors"
              data-testid={`quick-email-${lead.id}`}
              title="E-mail"
            >
              <Mail className="w-4 h-4" />
            </a>
          )}
          <button
            onClick={() => onOpenResponseHub(lead)}
            className="hidden sm:inline-flex items-center gap-1 px-3.5 h-9 rounded-xl text-[12px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
            data-testid={`open-lead-${lead.id}`}
          >
            Otwórz <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggle}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-zinc-400 hover:bg-zinc-100 transition-colors"
            data-testid={`expand-lead-${lead.id}`}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded: full message + actions */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-[#F0F1F3]">
              {/* Full message */}
              <div className="rounded-xl bg-[#FAFBFC] p-3 mt-3 mb-3">
                <p className="text-[13px] text-zinc-900 leading-relaxed">
                  {lead.message || 'Brak wiadomosci'}
                </p>
              </div>

              {/* Contact info */}
              <div className="flex items-center gap-4 mb-3 text-[12px] text-zinc-500">
                {lead.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {lead.email}
                  </span>
                )}
                {lead.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {lead.phone}
                  </span>
                )}
              </div>

              {/* Entity info */}
              {lead.entity_name && (
                <p className="text-[11px] text-zinc-400 mb-3">
                  Zapytanie o: {lead.entity_name}
                </p>
              )}

              {/* Lead Source — ROI visibility */}
              {(lead.utm_campaign || lead.source_type) && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 mb-3" data-testid={`lead-source-${lead.id}`}>
                  <p className="text-[11px] font-bold text-emerald-700 mb-1 flex items-center gap-1.5">
                    <Rocket className="w-3 h-3" /> Skąd przyszedł ten klient
                  </p>
                  {lead.utm_campaign && (
                    <p className="text-[12px] text-emerald-600">
                      Publikacja: <span className="font-semibold">{lead.utm_campaign}</span>
                    </p>
                  )}
                  <p className="text-[11px] text-emerald-500">
                    Źródło: {source}{lead.utm_source ? ` (${lead.utm_source})` : ''}
                  </p>
                </div>
              )}

              {/* FB Comment details (classification, confidence, suggested DM) */}
              {lead.source === 'facebook_comment' && (
                <div className="rounded-xl bg-blue-50/50 border border-blue-100 p-3 mb-3" data-testid={`comment-details-${lead.id}`}>
                  <p className="text-[11px] font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                    <MessageCircle className="w-3 h-3" /> Komentarz FB
                  </p>
                  {/* Original comment */}
                  <p className="text-[12px] text-zinc-700 bg-white rounded-lg px-3 py-2 mb-2 border border-blue-100">
                    &ldquo;{lead.source_comment_text || lead.message}&rdquo;
                  </p>
                  {/* Classification + confidence */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {lead.classification && CLASS_CONFIG[lead.classification] && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CLASS_CONFIG[lead.classification].bg} ${CLASS_CONFIG[lead.classification].text}`}>
                        {CLASS_CONFIG[lead.classification].label}
                      </span>
                    )}
                    {lead.classification_confidence && (
                      <span className="text-[10px] text-zinc-400">
                        Pewność: {Math.round(lead.classification_confidence * 100)}%
                      </span>
                    )}
                    {lead.dm_status && (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                        lead.dm_status === 'dm_sent' ? 'bg-emerald-100 text-emerald-700' :
                        lead.dm_status === 'manual_review' ? 'bg-blue-100 text-blue-700' :
                        lead.dm_status === 'dm_failed' ? 'bg-red-100 text-red-700' :
                        'bg-zinc-100 text-zinc-500'
                      }`}>
                        {lead.dm_status === 'dm_sent' ? 'DM wysłany' :
                         lead.dm_status === 'manual_review' ? 'Czeka na decyzję' :
                         lead.dm_status === 'dm_failed' ? 'DM błąd' :
                         lead.dm_status}
                      </span>
                    )}
                  </div>
                  {/* Send suggested DM button (for manual_review leads) */}
                  {lead.needs_manual_review && lead.dm_status !== 'dm_sent' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onSendSuggestedDM(lead.id); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                      data-testid={`send-dm-${lead.id}`}
                    >
                      <Send className="w-3 h-3" /> Wyślij sugerowany DM
                    </button>
                  )}
                </div>
              )}

              {/* Onboarding coach — tylko dla leada DEMO, jednorazowo */}
              {lead.is_demo && showDemoCoach && (
                <div className="mb-3 flex items-start gap-3 p-3 rounded-xl border border-[var(--apisai-primary)]/30 bg-[var(--apisai-primary)]/5" data-testid="demo-coach-bubble">
                  <span className="text-[18px] leading-none mt-0.5" role="img" aria-label="wave">👋</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-[var(--apisai-text-primary)]">To Twoje przykładowe zapytanie</p>
                    <p className="text-[12.5px] text-[var(--apisai-text-secondary)] mt-0.5 leading-relaxed">
                      Przećwicz obsługę klienta: kliknij <strong>„Szybka odpowiedź"</strong> albo zmień status poniżej (np. na <strong>„W kontakcie"</strong>). Gdy skończysz — usuń je ikoną kosza.
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissDemoCoach(); }}
                    className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-zinc-400 hover:bg-white/60 transition-colors"
                    data-testid="demo-coach-dismiss"
                    title="Zamknij wskazówkę"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Status pipeline — 4 stany: Nowe → W kontakcie → Rezerwacja → Zamknięte */}
              <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid={`lead-status-picker-${lead.id}`}>
                {PIPELINE_STATUSES.map((s) => {
                  const active = normalizeStatus(lead.status) === s.id;
                  const cfg = STATUS_MAP[s.id];
                  return (
                    <button
                      key={s.id}
                      onClick={(e) => { e.stopPropagation(); if (!active) onStatusChange(lead.id, s.id); }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${active ? 'text-white' : 'text-zinc-500 bg-zinc-100 hover:bg-zinc-200'}`}
                      style={active ? { background: cfg.dot } : undefined}
                      data-testid={`status-set-${s.id}-${lead.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? '#fff' : cfg.dot }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {/* CORE ACTIONS — WhatsApp + Email + Call + Response Hub */}
              <div className="flex items-center gap-2 flex-wrap" data-testid={`lead-actions-${lead.id}`}>
                {/* Szybka odpowiedź (Response Hub) */}
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenResponseHub(lead); }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold bg-[#F47A3B] text-white hover:bg-[#E06A2B] transition-colors"
                  data-testid={`action-response-hub-${lead.id}`}
                >
                  <Sparkles className="w-3.5 h-3.5" /> Szybka odpowiedź
                </button>

                {/* WhatsApp — tylko gdy istnieje poprawny numer (inaczej ukryty, brak martwej akcji) */}
                {whatsappLink && (
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.stopPropagation(); handleContactAction('whatsapp_clicked'); }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors"
                    data-testid={`action-whatsapp-${lead.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                )}

                {/* Zadzwoń — tylko gdy jest numer telefonu */}
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    onClick={(e) => { e.stopPropagation(); handleContactAction('call_clicked'); }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                    data-testid={`action-call-${lead.id}`}
                  >
                    <Phone className="w-3.5 h-3.5" /> Zadzwoń
                  </a>
                )}

                {/* Odpisz (Email) — tylko gdy jest adres e-mail */}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}?subject=Re: ${lead.entity_name || 'Zapytanie'}`}
                    onClick={(e) => { e.stopPropagation(); handleContactAction('email_clicked'); }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid={`action-reply-${lead.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> Odpisz
                  </a>
                )}

                {/* Oznacz jako klient */}
                {normalizeStatus(lead.status) !== 'closed' ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMarkAsClient(lead); }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold border border-[#10B981] text-[#10B981] hover:bg-[#F0FDF4] transition-colors ml-auto"
                    data-testid={`action-mark-client-${lead.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Oznacz jako klient
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold bg-[#F0FDF4] text-[#10B981] ml-auto" data-testid={`client-badge-${lead.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Klient
                    {lead.sale_value > 0 && <span className="ml-1">{lead.sale_value} zł</span>}
                  </span>
                )}

                {/* Usuń */}
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`action-delete-${lead.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* "Oznaczyć jako odpowiedziano?" hint */}
              <AnimatePresence>
                {showContactedHint && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-2 flex items-center gap-2 text-[11px] text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2"
                    data-testid={`contacted-hint-${lead.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Status zmieniony na "W trakcie"
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Response Hub Drawer ────────────────────────────── */
function ResponseHubDrawer({ lead, isOpen, onClose, onStatusChange }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [toneLoading, setToneLoading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);
  const token = localStorage.getItem('token');
  const whatsappLink = buildWhatsAppLink(lead?.phone, editedText || '');

  useEffect(() => {
    if (isOpen && lead) {
      axios.get(`${API}/leads/quick-replies?lead_id=${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const t = res.data?.templates || [];
        setTemplates(t);
        if (t.length > 0) setSelectedTemplate(t[0]);
        setProfileComplete(res.data?.profile_complete ?? true);
      }).catch(() => {});
    }
  }, [isOpen, lead?.id]); // eslint-disable-line

  useEffect(() => { setEditedText(selectedTemplate?.text || ''); }, [selectedTemplate?.id]); // eslint-disable-line

  const handleDraft = async () => {
    if (drafting || !lead) return;
    setDrafting(true);
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/draft-reply`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const text = res.data?.reply;
      if (text) {
        const draftT = { id: 'asystent_draft', label: 'Napisz za mnie', text };
        setTemplates(prev => [draftT, ...prev.filter(t => t.id !== 'asystent_draft')]);
        setSelectedTemplate(draftT);
        setEditedText(text);
        logEvent('reply_drafted');
        toast.success('Asystent przygotował odpowiedź');
      }
    } catch {
      toast.error('Nie udało się przygotować odpowiedzi');
    } finally { setDrafting(false); }
  };

  const handleTone = async (toneId) => {
    if (toneLoading || !lead || !editedText) return;
    setToneLoading(toneId);
    try {
      const res = await axios.post(
        `${API}/leads/${lead.id}/draft-reply`,
        { tone: toneId, current: editedText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const text = res.data?.reply;
      if (text) {
        const draftT = { id: 'asystent_draft', label: 'Napisz za mnie', text };
        setTemplates(prev => [draftT, ...prev.filter(t => t.id !== 'asystent_draft')]);
        setSelectedTemplate(draftT);
        setEditedText(text);
        logEvent('reply_tone_' + toneId);
      }
    } catch {
      toast.error('Nie udało się zmienić tonu');
    } finally { setToneLoading(null); }
  };

  const logEvent = async (eventType) => {
    try {
      await axios.post(`${API}/leads/${lead.id}/event`, {
        event_type: eventType,
        metadata: { source: 'response_hub' }
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silent */ }
  };

  const handleCopy = () => {
    if (!editedText) return;
    navigator.clipboard.writeText(editedText);
    setCopied(true);
    logEvent('reply_copied');
    toast.success('Skopiowano odpowiedź');
    setTimeout(() => setCopied(false), 2000);
    // Prompt status change
    if (normalizeStatus(lead.status) === 'new') {
      onStatusChange(lead.id, 'in_progress');
    }
  };

  const handleWhatsApp = () => {
    logEvent('whatsapp_clicked');
    if (normalizeStatus(lead.status) === 'new') onStatusChange(lead.id, 'in_progress');
  };

  const handleEmail = () => {
    logEvent('email_clicked');
    if (normalizeStatus(lead.status) === 'new') onStatusChange(lead.id, 'in_progress');
  };

  if (!lead) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-50"
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto"
            data-testid="response-hub-drawer"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-4 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[16px] font-bold text-zinc-900">Szybka odpowiedź</h2>
                  <p className="text-[12px] text-zinc-500">dla {lead.name || 'Klient'}</p>
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center">
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Profile completeness badge */}
              {!profileComplete && (
                <Link to="/panel/profil?tab=responses" onClick={onClose} data-testid="response-profile-badge">
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                    <p className="text-[12px] text-amber-700">Uzupełnij profil odpowiedzi — szablony będą lepiej dopasowane</p>
                    <ChevronRight className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 ml-auto" />
                  </div>
                </Link>
              )}
              {profileComplete && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="response-profile-badge-ok">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  <p className="text-[12px] text-emerald-700">Profil odpowiedzi: gotowy</p>
                </div>
              )}

              {/* Lead info card */}
              <div className="rounded-xl bg-zinc-50 p-4 border border-zinc-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#F47A3B] text-white flex items-center justify-center font-bold text-sm">
                    {(lead.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-zinc-900">{lead.name}</p>
                    <p className="text-[11px] text-zinc-500">{lead.entity_name ? `Zapytanie o: ${lead.entity_name}` : lead.email}</p>
                  </div>
                </div>
                <p className="text-[12px] text-zinc-600 italic">&ldquo;{lead.message || 'Brak wiadomości'}&rdquo;</p>
              </div>

              {/* Napisz za mnie — asystent pisze odpowiedź na treść klienta */}
              <button
                onClick={handleDraft}
                disabled={drafting}
                data-testid="response-hub-draft-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-[#F47A3B] text-[#F47A3B] hover:bg-[#F47A3B]/5 disabled:opacity-50 transition-colors"
              >
                {drafting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Asystent pisze…</>) : (<><Sparkles className="w-4 h-4" /> Napisz za mnie</>)}
              </button>

              {/* Warianty tonu — dostosuj wygenerowaną odpowiedź */}
              {editedText && (
                <div className="flex flex-wrap items-center gap-2" data-testid="tone-variants">
                  <span className="text-[11px] text-zinc-500">Dostosuj ton:</span>
                  {[
                    { id: 'warmer', label: 'Cieplej' },
                    { id: 'shorter', label: 'Krócej' },
                    { id: 'formal', label: 'Bardziej formalnie' },
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => handleTone(t.id)}
                      disabled={!!toneLoading}
                      data-testid={`tone-${t.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border border-zinc-200 text-zinc-700 hover:border-[#F47A3B] hover:text-[#F47A3B] disabled:opacity-50 transition-colors"
                    >
                      {toneLoading === t.id && <Loader2 className="w-3 h-3 animate-spin" />}
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Template picker */}
              <div>
                <p className="text-[13px] font-bold text-zinc-900 mb-2">Wybierz szablon</p>
                <div className="space-y-2">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTemplate(t)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        selectedTemplate?.id === t.id
                          ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      <p className={`text-[12px] font-semibold mb-1 ${selectedTemplate?.id === t.id ? 'text-[#F47A3B]' : 'text-zinc-900'}`}>
                        {t.label}
                      </p>
                      <p className="text-[11px] text-zinc-500 line-clamp-2">{t.text}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview — edytowalny */}
              {(selectedTemplate || editedText) && (
                <div>
                  <p className="text-[13px] font-bold text-zinc-900 mb-2">Podgląd odpowiedzi <span className="font-normal text-zinc-400">(możesz edytować)</span></p>
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    rows={6}
                    className="w-full rounded-xl bg-white border border-zinc-200 p-4 text-[13px] text-zinc-700 leading-relaxed focus:border-[#F47A3B] focus:outline-none focus:ring-1 focus:ring-[#F47A3B]/20 resize-none"
                    data-testid="response-hub-preview-text"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleCopy}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[13px] font-semibold bg-[#F47A3B] text-white hover:bg-[#E06A2B] transition-colors"
                  data-testid="response-hub-copy"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Skopiowano!' : 'Kopiuj odpowiedź'}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  {whatsappLink && (
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleWhatsApp}
                      className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[12px] font-semibold bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors"
                      data-testid="response-hub-whatsapp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  )}

                  {lead.email && (
                    <a
                      href={`mailto:${lead.email}?subject=Re: ${lead.entity_name || 'Zapytanie'}&body=${encodeURIComponent(editedText || '')}`}
                      onClick={handleEmail}
                      className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[12px] font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                      data-testid="response-hub-email"
                    >
                      <Mail className="w-3.5 h-3.5" /> Wyślij email
                    </a>
                  )}
                </div>

                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    onClick={() => { logEvent('call_clicked'); if (normalizeStatus(lead.status) === 'new') onStatusChange(lead.id, 'in_progress'); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="response-hub-call"
                  >
                    <Phone className="w-3.5 h-3.5" /> Zadzwoń: {lead.phone}
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ── Pasek 4 kart statystyk (1:1 z makietą) ─────────── */
function LeadsStatsBar({ tiles, onTileClick, activeFilter }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="leads-stats-bar">
      {tiles.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => onTileClick && s.filterId && onTileClick(s.filterId)}
          className={`apisai-soft-card p-5 flex gap-4 text-left transition-all hover:-translate-y-0.5 ${activeFilter === s.filterId ? 'ring-2 ring-[var(--apisai-primary)]/40' : ''}`}
          data-testid={`lead-stat-${s.key}`}
        >
          <div
            className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: s.iconBg }}
          >
            <s.Icon className="w-5 h-5" style={{ color: s.iconColor }} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[var(--apisai-text-primary)] text-[28px] font-bold leading-none tracking-tight">
              {s.value}
            </p>
            <p className="text-[var(--apisai-text-secondary)] text-[13px] font-medium mt-1.5 leading-snug">
              {s.label}
            </p>
            {s.sub && (
              <p className="text-[11px] font-semibold mt-1.5" style={{ color: s.subColor }}>
                {s.sub}
              </p>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── Box „Jak to działa?" (prawy górny róg) ─────────── */
function InquiriesHelpBox() {
  const navigate = useNavigate();
  return (
    <div className="apisai-soft-card p-5 max-w-sm w-full" data-testid="inquiries-help-box">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--apisai-primary)]/10 flex items-center justify-center">
          <Lightbulb className="w-5 h-5 text-[var(--apisai-primary)]" />
        </div>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-[var(--apisai-text-primary)]">Jak to działa?</p>
          <p className="text-[12.5px] text-[var(--apisai-text-secondary)] mt-1 leading-relaxed">
            Po publikacji Twojej strony lub oferty klienci będą mogli wysyłać zapytania. Wszystkie wiadomości znajdziesz tutaj.
          </p>
          <button
            type="button"
            onClick={() => navigate('/pomoc')}
            className="inline-flex items-center gap-1 mt-2 text-[13px] font-semibold text-[var(--apisai-primary)] hover:gap-1.5 transition-all"
            data-testid="help-guide-link"
          >
            Zobacz poradnik <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Karta przykładowego zapytania (DEMO) ───────────── */
function DemoLeadCard({ onSend, sending }) {
  return (
    <div className="apisai-soft-card p-5 relative" data-testid="demo-lead-card">
      <span className="absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">DEMO</span>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[var(--apisai-primary)]/10 flex items-center justify-center text-[var(--apisai-primary)] font-bold flex-shrink-0">
          AK
        </div>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-[var(--apisai-text-primary)]">Anna Kowalska</p>
          <p className="text-[12.5px] text-[var(--apisai-text-secondary)]">annakowalska@email.pl · +48 600 123 456</p>
        </div>
      </div>
      <div className="mt-4 space-y-1.5 text-[13px]">
        <div className="flex gap-2"><span className="text-zinc-400 w-16 flex-shrink-0">Status:</span><span className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full" style={{ color: '#3B82F6', background: '#EFF6FF' }}>Nowe</span></div>
        <div className="flex gap-2"><span className="text-zinc-400 w-16 flex-shrink-0">Pyta o:</span><span className="text-[var(--apisai-text-primary)] font-medium">Miot owczarka niemieckiego</span></div>
        <div className="flex gap-2"><span className="text-zinc-400 w-16 flex-shrink-0">Źródło:</span><span className="text-[var(--apisai-text-primary)]">Strona miotu</span></div>
        <div className="flex gap-2"><span className="text-zinc-400 w-16 flex-shrink-0">Data:</span><span className="text-[var(--apisai-text-primary)]">12.05.2024, 14:32</span></div>
      </div>
      <div className="mt-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 text-[13px] text-zinc-600 leading-relaxed">
        Dzień dobry, chciałabym zapytać o dostępność suczki z aktualnego miotu. Interesuje mnie szczeniak długowłosy. Proszę o więcej informacji.
      </div>
      <div className="mt-4">
        <button
          type="button"
          onClick={onSend}
          disabled={sending}
          data-testid="send-demo-lead-btn"
          className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-[var(--apisai-primary)] text-white text-[13px] font-semibold hover:brightness-95 transition-all disabled:opacity-60"
        >
          <Send className="w-4 h-4" />
          {sending ? 'Wysyłam…' : 'Wyślij testowe zapytanie do siebie'}
        </button>
        <p className="text-[11px] text-zinc-400 text-center mt-2">
          Dodamy to przykładowe zapytanie do Twojej listy, żebyś przećwiczył obsługę. Nie wpływa na statystyki — usuniesz je jednym kliknięciem.
        </p>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────── */
export default function LeadsManager() {
  const { t } = useTranslation();
  const { leadId: routeLeadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [responseHubLead, setResponseHubLead] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // lead id to confirm delete
  const [clientDialog, setClientDialog] = useState(null); // lead object for "mark as client"
  const [saleValue, setSaleValue] = useState('');
  const [salesSummary, setSalesSummary] = useState(null);
  const [successLoop, setSuccessLoop] = useState(null); // { name, value } after marking client
  const [dashStats, setDashStats] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showType, setShowType] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', email: '', message: '', entity_name: '' });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [sendingDemo, setSendingDemo] = useState(false);
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchSalesSummary();
    fetchDashStats();
  }, []);

  // Escape zamyka otwarte modale/panel (spójność UX)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowAddLead(false);
        setDeleteConfirm(null);
        setClientDialog(null);
        setResponseHubLead(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Deep link: /panel/leady/:id OR ?lead=id (fallback)
  useEffect(() => {
    const targetId = routeLeadId || searchParams.get('lead');
    if (targetId && leads.length > 0) {
      setExpandedId(targetId);
      setActiveFilter('all');
      // Redirect query param to clean URL
      if (searchParams.get('lead') && !routeLeadId) {
        navigate(`/panel/leady/${targetId}`, { replace: true });
      }
    }
  }, [routeLeadId, searchParams, leads]);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/panel/leads/all`, { headers: authHeaders });
      setLeads(res.data || []);
    } catch {
      toast.error('Błąd ładowania zapytań');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/panel/leads/campaign-feedback`, { headers: authHeaders });
      setStats(res.data);
    } catch {}
  };

  // Wyślij testowe zapytanie do siebie (DEMO) — nie wpływa na KPI.
  const handleSendDemoLead = async () => {
    setSendingDemo(true);
    try {
      const res = await axios.post(`${API}/leads/demo`, {}, { headers: authHeaders });
      await fetchLeads();
      if (res.data?.already_exists) {
        toast.info('Masz już przykładowe zapytanie — znajdziesz je na liście.');
      } else {
        toast.success('Gotowe! Przykładowe zapytanie pojawiło się na liście — obsłuż je jak prawdziwe.');
      }
      if (res.data?.id) setExpandedId(res.data.id);
    } catch {
      toast.error('Nie udało się utworzyć przykładowego zapytania');
    } finally {
      setSendingDemo(false);
    }
  };

  const fetchDashStats = async () => {
    try {
      const res = await axios.get(`${API}/panel/leads/dashboard-stats`, { headers: authHeaders });
      setDashStats(res.data);
    } catch {}
  };

  const submitManualLead = async () => {
    if (!addForm.name.trim()) { toast.error('Podaj imię klienta'); return; }
    setAddSubmitting(true);
    try {
      await axios.post(`${API}/panel/leads/manual`, addForm, { headers: authHeaders });
      toast.success('Zapytanie dodane');
      setShowAddLead(false);
      setAddForm({ name: '', phone: '', email: '', message: '', entity_name: '' });
      fetchLeads();
      fetchDashStats();
    } catch {
      toast.error('Nie udało się dodać zapytania');
    } finally {
      setAddSubmitting(false);
    }
  };

  const fetchSalesSummary = async () => {
    try {
      const res = await axios.get(`${API}/panel/leads/sales-summary`, { headers: authHeaders });
      setSalesSummary(res.data);
    } catch {}
  };

  const handleSendSuggestedDM = async (leadId) => {
    try {
      const res = await axios.post(`${API}/social/dm/send-suggested`, { lead_id: leadId }, { headers: authHeaders });
      if (res.data.ok) {
        toast.success('DM wysłany!');
        fetchLeads();
      } else {
        toast.error(`DM nie wysłany: ${res.data.status}`);
      }
    } catch {
      toast.error('Błąd wysyłania DM');
    }
  };

  const handleOpenResponseHub = (lead) => {
    setResponseHubLead(lead);
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus }, { headers: authHeaders });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      const statusLabel = STATUS_MAP[newStatus]?.labelKey ? t(STATUS_MAP[newStatus].labelKey) : newStatus;
      toast.success(`Status: ${statusLabel}`);
    } catch {
      toast.error('Blad zmiany statusu');
    }
  };

  const markAsClient = async () => {
    if (!clientDialog) return;
    try {
      const payload = { status: 'closed' };
      const val = parseFloat(saleValue);
      if (!isNaN(val) && val > 0) payload.sale_value = val;
      await axios.patch(`${API}/leads/${clientDialog.id}/status`, payload, { headers: authHeaders });
      setLeads(prev => prev.map(l =>
        l.id === clientDialog.id ? { ...l, status: 'closed', sale_value: payload.sale_value || 0 } : l
      ));

      // Fetch fresh sales summary for the modal
      let freshSales = salesSummary;
      try {
        const res = await axios.get(`${API}/panel/leads/sales-summary`, { headers: authHeaders });
        freshSales = res.data;
        setSalesSummary(res.data);
      } catch {}

      setSuccessLoop({
        name: clientDialog.name || 'Klient',
        value: payload.sale_value || 0,
        source: clientDialog.utm_campaign || null,
        sourceType: clientDialog.source_type || null,
        totalClients: freshSales?.clients_count || 1,
        totalValue: freshSales?.total_value || 0,
      });
      setClientDialog(null);
      setSaleValue('');
    } catch {
      toast.error('Błąd zapisu');
    }
  };

  const deleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/panel/leads/${leadId}`, { headers: authHeaders });
      setLeads(prev => prev.filter(l => l.id !== leadId));
      setDeleteConfirm(null);
      if (expandedId === leadId) setExpandedId(null);
      toast.success('Lead usunięty');
    } catch {
      toast.error('Błąd usuwania leada');
    }
  };

  const newCount = leads.filter(l => normalizeStatus(l.status) === 'new').length;
  const inProgressCount = leads.filter(l => normalizeStatus(l.status) === 'in_progress').length;
  const manualReviewCount = leads.filter(l => l.needs_manual_review === true && l.source === 'facebook_comment').length;

  // Filtrowanie (tab + źródło + typ) + sortowanie
  const filtered = leads
    .filter(lead => {
      if (activeFilter === 'manual_review') return lead.needs_manual_review === true && lead.source === 'facebook_comment';
      if (activeFilter !== 'all' && normalizeStatus(lead.status) !== activeFilter) return false;
      if (sourceFilter !== 'all' && leadZrodlo(lead) !== sourceFilter) return false;
      if (typeFilter !== 'all' && leadTyp(lead) !== typeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sortOrder === 'oldest' ? ta - tb : tb - ta;
    });

  const reservedCount = leads.filter(l => normalizeStatus(l.status) === 'reserved').length;
  const closedCount = leads.filter(l => normalizeStatus(l.status) === 'closed').length;

  // 4 karty statusów — 1:1 z makietą (Nowe / W kontakcie / Rezerwacje / Zamknięte)
  const statTiles = [
    { key: 'new', filterId: 'new', Icon: Mail, iconColor: '#F47A3B', iconBg: 'rgba(244,122,59,0.1)',
      value: newCount, label: 'Nowe', sub: newCount ? `${newCount} oczekuje` : 'Brak nowych zapytań', subColor: '#71717A' },
    { key: 'in_progress', filterId: 'in_progress', Icon: MessageCircle, iconColor: '#3B82F6', iconBg: 'rgba(59,130,246,0.1)',
      value: inProgressCount, label: 'W kontakcie', sub: inProgressCount ? 'aktywne rozmowy' : 'Brak aktywnych rozmów', subColor: '#71717A' },
    { key: 'reserved', filterId: 'reserved', Icon: PawPrint, iconColor: '#8B5CF6', iconBg: 'rgba(139,92,246,0.1)',
      value: reservedCount, label: 'Rezerwacje', sub: reservedCount ? 'zarezerwowane' : 'Brak rezerwacji', subColor: '#71717A' },
    { key: 'closed', filterId: 'closed', Icon: CheckCircle2, iconColor: '#10B981', iconBg: 'rgba(16,185,129,0.1)',
      value: closedCount, label: 'Zamknięte', sub: closedCount ? 'zakończone' : 'Brak zamkniętych rozmów', subColor: '#71717A' },
  ];
  const displayedTabs = FILTER_TABS;

  // ── Loading ──
  if (loading) {
    return (
      <StudioShell mode="workspace" activeKey="messages" contextLabel="Zapytania">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-3 border-[#F47A3B] border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </StudioShell>
    );
  }

  // ── Main view (z listą LUB bogatym pustym stanem) ──
  return (
    <StudioShell mode="workspace" activeKey="messages" contextLabel="Zapytania">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8 space-y-6" data-testid="leads-dashboard">

        {/* Nagłówek + box pomocy */}
        <header className="flex items-start justify-between gap-6 flex-wrap">
          <div className="space-y-2 max-w-2xl">
            <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight flex items-center gap-2">
              Zapytania od klientów <MessageCircle className="w-7 h-7 text-[var(--apisai-primary)]" />
            </h1>
            <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-[15px] leading-relaxed">
              Tutaj trafiają wiadomości od osób zainteresowanych Twoją hodowlą, miotami, szczeniętami lub reproduktorem. Możesz oznaczać status rozmowy, odpowiadać szybciej i pilnować kontaktu z klientami.
            </p>
          </div>
          <InquiriesHelpBox />
        </header>

        {/* Pasek 4 kart statystyk */}
        <LeadsStatsBar tiles={statTiles} onTileClick={setActiveFilter} activeFilter={activeFilter} />

        {/* Miękki kremowy alert — nowi klienci */}
        {newCount > 0 && (
          <div
            className="flex items-center gap-3 rounded-2xl px-5 py-4 border"
            style={{ background: '#FFF8F1', borderColor: 'rgba(249,115,22,0.18)' }}
            data-testid="new-leads-alert"
          >
            <div
              className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(249,115,22,0.12)' }}
            >
              <Flame className="w-5 h-5 text-[var(--apisai-primary)]" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[var(--apisai-text-primary)] text-[15px] font-bold">
                {newCount === 1 ? '1 nowy klient!' : `${newCount} nowych klientów!`}
              </p>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-0.5 leading-relaxed">
                Masz nowe zapytania — odpowiedz szybko, zanim klienci stracą zainteresowanie.
              </p>
            </div>
            <button
              onClick={() => setActiveFilter('new')}
              className="flex-shrink-0 inline-flex items-center px-4 h-10 rounded-xl border border-[var(--apisai-primary)] text-[var(--apisai-primary)] text-[13px] font-semibold hover:bg-[var(--apisai-primary)]/5 transition-colors"
              data-testid="see-new-leads-btn"
            >
              Zobacz nowe
            </button>
          </div>
        )}

        {/* Miękki alert — leady do sprawdzenia (komentarze FB) */}
        {manualReviewCount > 0 && (
          <button
            type="button"
            onClick={() => setActiveFilter('manual_review')}
            className="w-full flex items-center gap-3 rounded-2xl px-5 py-3.5 border text-left transition-colors hover:brightness-[0.99]"
            style={{ background: '#F4FAFF', borderColor: 'rgba(14,165,233,0.18)' }}
            data-testid="manual-review-alert"
          >
            <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse flex-shrink-0" />
            <span className="text-[13px] font-semibold text-sky-700">
              {manualReviewCount} {manualReviewCount === 1 ? 'lead czeka' : 'leadów czeka'} na Twoją decyzję
            </span>
            <span className="text-[12px] text-sky-500 ml-auto inline-flex items-center gap-1">
              Sprawdź <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </button>
        )}

        {/* Taby + sortowanie/filtry */}
        <div className="relative flex items-end justify-between gap-3 border-b border-[var(--polish-border,rgba(0,0,0,0.08))] flex-wrap">
          {(showSort || showFilter || showType) && (
            <div className="fixed inset-0 z-10" onClick={() => { setShowSort(false); setShowFilter(false); setShowType(false); }} />
          )}
          <div className="flex gap-6 overflow-x-auto" data-testid="leads-filter-tabs">
          {displayedTabs.map(tab => {
            const count = tab.id === 'all'
              ? leads.length
              : tab.id === 'manual_review'
                ? manualReviewCount
                : leads.filter(l => normalizeStatus(l.status) === tab.id).length;
            const isActive = activeFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`flex items-center gap-1.5 px-1 pb-3 text-[13px] font-semibold whitespace-nowrap transition-colors relative ${
                  isActive
                    ? 'text-[var(--apisai-primary)]'
                    : 'text-[var(--apisai-text-muted)] hover:text-[var(--apisai-text-primary)]'
                }`}
                data-testid={`filter-${tab.id}`}
              >
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-[var(--apisai-primary)]/10 text-[var(--apisai-primary)]' : 'bg-zinc-100 text-zinc-400'
                }`}>
                  {count}
                </span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--apisai-primary)] rounded-full" />
                )}
              </button>
            );
          })}
          </div>

          {/* Filtry: Źródło · Typ · Data + Dodaj */}
          <div className="flex items-center gap-2 pb-2 flex-wrap">
            {/* Źródło */}
            <div className="relative">
              <button
                onClick={() => { setShowFilter(s => !s); setShowSort(false); setShowType(false); }}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border text-[13px] font-medium hover:bg-zinc-50 transition-colors ${sourceFilter !== 'all' ? 'border-[var(--apisai-primary)] text-[var(--apisai-primary)]' : 'border-zinc-200 text-zinc-600'}`}
                data-testid="filter-source-btn"
              >
                Źródło: {SOURCE_FILTER_OPTIONS.find(o => o.id === sourceFilter)?.label || 'Wszystkie'}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showFilter && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl border border-zinc-200 shadow-lg py-1 z-20">
                  {SOURCE_FILTER_OPTIONS.map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setSourceFilter(o.id); setShowFilter(false); }}
                      className={`w-full text-left px-3 py-2 text-[13px] hover:bg-zinc-50 ${sourceFilter === o.id ? 'text-[var(--apisai-primary)] font-semibold' : 'text-zinc-600'}`}
                      data-testid={`filter-source-opt-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Typ */}
            <div className="relative">
              <button
                onClick={() => { setShowType(s => !s); setShowSort(false); setShowFilter(false); }}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border text-[13px] font-medium hover:bg-zinc-50 transition-colors ${typeFilter !== 'all' ? 'border-[var(--apisai-primary)] text-[var(--apisai-primary)]' : 'border-zinc-200 text-zinc-600'}`}
                data-testid="filter-type-btn"
              >
                Typ: {TYPE_FILTER_OPTIONS.find(o => o.id === typeFilter)?.label || 'Wszystkie'}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showType && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-zinc-200 shadow-lg py-1 z-20">
                  {TYPE_FILTER_OPTIONS.map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setTypeFilter(o.id); setShowType(false); }}
                      className={`w-full text-left px-3 py-2 text-[13px] hover:bg-zinc-50 ${typeFilter === o.id ? 'text-[var(--apisai-primary)] font-semibold' : 'text-zinc-600'}`}
                      data-testid={`filter-type-opt-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Data */}
            <div className="relative">
              <button
                onClick={() => { setShowSort(s => !s); setShowFilter(false); setShowType(false); }}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-zinc-200 text-[13px] font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
                data-testid="sort-btn"
              >
                <Calendar className="w-3.5 h-3.5" /> {sortOrder === 'newest' ? 'Od najnowszych' : 'Od najstarszych'}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showSort && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-zinc-200 shadow-lg py-1 z-20">
                  {[{ id: 'newest', label: 'Od najnowszych' }, { id: 'oldest', label: 'Od najstarszych' }].map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setSortOrder(o.id); setShowSort(false); }}
                      className={`w-full text-left px-3 py-2 text-[13px] hover:bg-zinc-50 ${sortOrder === o.id ? 'text-[var(--apisai-primary)] font-semibold' : 'text-zinc-600'}`}
                      data-testid={`sort-opt-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Dodaj zapytanie ręcznie */}
            <button
              onClick={() => setShowAddLead(true)}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[var(--apisai-primary)] text-white text-[13px] font-semibold hover:brightness-95 transition-all"
              data-testid="add-lead-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Dodaj
            </button>
          </div>
        </div>

        {/* Lista zapytań / bogaty pusty stan */}
        {leads.length === 0 ? (
          <div data-testid="leads-empty">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEWA: edukacyjny pusty stan */}
              <div className="apisai-soft-card p-8" data-testid="leads-empty-educational">
                <div className="w-14 h-14 rounded-2xl bg-[var(--apisai-primary)]/10 flex items-center justify-center mb-5">
                  <Mail className="w-7 h-7 text-[var(--apisai-primary)]" />
                </div>
                <h2 className="text-[20px] font-bold text-[var(--apisai-text-primary)] mb-2">Nie masz jeszcze zapytań</h2>
                <p className="text-[14px] text-[var(--apisai-text-secondary)] leading-relaxed mb-5">
                  Po publikacji strony hodowli, miotu lub reproduktora wiadomości od klientów będą trafiały tutaj.
                </p>
                <ul className="space-y-2.5 mb-7">
                  {[
                    'Klienci znajdą Twoją stronę w katalogu APISAI',
                    'Będą mogli wysłać zapytanie przez formularz',
                    'Wszystkie wiadomości pojawią się w tym miejscu',
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-[var(--apisai-text-secondary)]">
                      <CheckCircle2 className="w-[18px] h-[18px] text-[var(--apisai-primary)] flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wide mb-3">Co możesz zrobić teraz?</p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button onClick={() => navigate('/wizard/hodowla-v2')} data-testid="empty-cta-kennel"
                    className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl bg-[var(--apisai-primary)] text-white text-[12.5px] font-semibold text-center hover:brightness-95 transition-all">
                    <Home className="w-5 h-5" /> Opublikuj stronę hodowli
                  </button>
                  <button onClick={() => navigate('/wizard/miot')} data-testid="empty-cta-litter"
                    className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-[var(--apisai-primary)]/40 text-[var(--apisai-primary)] text-[12.5px] font-semibold text-center hover:bg-[var(--apisai-primary)]/5 transition-all">
                    <Baby className="w-5 h-5" /> Dodaj miot
                  </button>
                  <button onClick={() => navigate('/wizard/reproduktor')} data-testid="empty-cta-stud"
                    className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-[var(--apisai-primary)]/40 text-[var(--apisai-primary)] text-[12.5px] font-semibold text-center hover:bg-[var(--apisai-primary)]/5 transition-all">
                    <Dog className="w-5 h-5" /> Dodaj reproduktora
                  </button>
                  <button onClick={handleSendDemoLead} disabled={sendingDemo} data-testid="empty-cta-demo"
                    className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-zinc-200 text-zinc-500 text-[12.5px] font-semibold text-center hover:bg-zinc-50 transition-all disabled:opacity-60">
                    <Eye className="w-5 h-5" /> Zobacz przykładowe zapytanie
                  </button>
                </div>
              </div>
              {/* PRAWA: karta DEMO */}
              <DemoLeadCard onSend={handleSendDemoLead} sending={sendingDemo} />
            </div>
            {/* Box pomocy (dół) */}
            <div className="apisai-soft-card p-4 flex items-center gap-2 mt-6" data-testid="leads-help-bar">
              <HelpCircle className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              <p className="text-[13px] text-[var(--apisai-text-secondary)]">
                Masz pytania?{' '}
                <button onClick={() => navigate('/pomoc')} className="text-[var(--apisai-primary)] font-medium hover:underline">
                  Skorzystaj z pomocy
                </button>{' '}lub zobacz poradnik obsługi zapytań.
              </p>
              <button onClick={() => navigate('/pomoc')} className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:gap-1.5 transition-all">
                Pomoc <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="apisai-soft-card p-10 text-center" data-testid="leads-empty-filtered">
            <Inbox className="w-8 h-8 text-[#E8EAED] mx-auto mb-2" />
            <p className="text-[13px] text-zinc-400">Brak zapytań pasujących do wybranych filtrów.</p>
          </div>
        ) : (
          <div className="apisai-soft-card overflow-hidden divide-y divide-[#F0F1F3]" data-testid="leads-list">
            {filtered.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                isExpanded={expandedId === lead.id}
                onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                onStatusChange={updateStatus}
                onOpenResponseHub={handleOpenResponseHub}
                onDelete={(id) => setDeleteConfirm(id)}
                onMarkAsClient={(l) => { setClientDialog(l); setSaleValue(''); }}
                onSendSuggestedDM={handleSendSuggestedDM}
              />
            ))}
          </div>
        )}

        {/* Wskazówka — tylko gdy są zapytania */}
        {leads.length > 0 && (
        <div className="apisai-soft-card p-5 flex items-center gap-4" data-testid="leads-tip">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(244,122,59,0.1)' }}>
            <Lightbulb className="w-5 h-5 text-[var(--apisai-primary)]" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold text-[var(--apisai-text-primary)]">Wskazówka</p>
            <p className="text-[13px] text-[var(--apisai-text-secondary)] mt-0.5">
              Odpowiadaj szybko — pierwsza odpowiedź w ciągu 15 minut zwiększa szanse na sprzedaż aż o 300%.
            </p>
          </div>
        </div>
        )}
      </div>

      {/* Modal: Nowe zapytanie (ręczne) */}
      <AnimatePresence>
        {showAddLead && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50"
              onClick={() => setShowAddLead(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              data-testid="add-lead-dialog"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[17px] font-bold text-zinc-900">Nowe zapytanie</h3>
                  <button onClick={() => setShowAddLead(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:bg-zinc-100" data-testid="add-lead-close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[13px] text-zinc-500 mb-4">Dodaj klienta, który skontaktował się z Tobą poza systemem (telefon, targi, polecenie).</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[12px] font-medium text-zinc-600 mb-1 block">Imię i nazwisko *</label>
                    <input
                      value={addForm.name}
                      onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="np. Anna Kowalska"
                      className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
                      data-testid="add-lead-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[12px] font-medium text-zinc-600 mb-1 block">Telefon</label>
                      <input
                        value={addForm.phone}
                        onChange={(e) => setAddForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="+48 600..."
                        className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
                        data-testid="add-lead-phone"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] font-medium text-zinc-600 mb-1 block">E-mail</label>
                      <input
                        value={addForm.email}
                        onChange={(e) => setAddForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="email@..."
                        className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
                        data-testid="add-lead-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-zinc-600 mb-1 block">Czego dotyczy (opcjonalnie)</label>
                    <input
                      value={addForm.entity_name}
                      onChange={(e) => setAddForm(f => ({ ...f, entity_name: e.target.value }))}
                      placeholder="np. Miot Golden Retriever"
                      className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B]"
                      data-testid="add-lead-entity"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-zinc-600 mb-1 block">Wiadomość</label>
                    <textarea
                      value={addForm.message}
                      onChange={(e) => setAddForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Notatka o kliencie / treść zapytania"
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B] resize-none"
                      data-testid="add-lead-message"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => setShowAddLead(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="add-lead-cancel"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={submitManualLead}
                    disabled={addSubmitting}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-[#F47A3B] text-white hover:bg-[#E06A2B] transition-colors disabled:opacity-60"
                    data-testid="add-lead-submit"
                  >
                    {addSubmitting ? 'Dodawanie…' : 'Dodaj zapytanie'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete confirmation dialog */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50"
              onClick={() => setDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              data-testid="delete-confirm-dialog"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-zinc-900">Usunąć lead?</h3>
                    <p className="text-[12px] text-zinc-500">Tej operacji nie można cofnąć.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="delete-cancel-btn"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={() => deleteLead(deleteConfirm)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                    data-testid="delete-confirm-btn"
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mark as client dialog */}
      <AnimatePresence>
        {clientDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50"
              onClick={() => setClientDialog(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              data-testid="client-dialog"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-zinc-900">Czy doszło do sprzedaży?</h3>
                    <p className="text-[12px] text-zinc-500">{clientDialog.name || 'Klient'}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-[12px] font-medium text-zinc-600 mb-1.5 block">Kwota sprzedaży (opcjonalne)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={saleValue}
                      onChange={(e) => setSaleValue(e.target.value)}
                      placeholder="np. 3000"
                      className="w-full px-4 py-2.5 pr-10 rounded-xl border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
                      data-testid="sale-value-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-zinc-400 font-medium">zł</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setClientDialog(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="client-cancel-btn"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={markAsClient}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                    data-testid="client-confirm-btn"
                  >
                    Zapisz
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Success Loop — celebration after marking client */}
      <AnimatePresence>
        {successLoop && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
              onClick={() => setSuccessLoop(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="fixed inset-0 z-[60] flex items-center justify-center p-4"
              data-testid="success-loop-modal"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
                {/* Animated icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                >
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="text-2xl font-extrabold text-zinc-900 mb-1"
                >
                  Masz klienta!
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.42 }}
                  className="text-[15px] text-zinc-500 mb-3"
                >
                  {successLoop.name}
                  {successLoop.value > 0 && (
                    <span className="font-bold text-emerald-600"> — {successLoop.value.toLocaleString('pl-PL')} zł</span>
                  )}
                </motion.p>

                {/* Source — killer detail */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-100 mb-4"
                  data-testid="success-source"
                >
                  <Rocket className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="text-[12px] text-emerald-700 font-medium">
                    {successLoop.source
                      ? <>To zapytanie przyszło z publikacji: <b>{successLoop.source}</b></>
                      : 'To zapytanie przyszło z Twojej publikacji'}
                  </span>
                </motion.div>

                {/* Counter — total clients + revenue */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.58 }}
                  className="flex items-center justify-center gap-4 mb-5"
                >
                  <div className="text-center px-4 py-2 rounded-xl bg-zinc-50">
                    <p className="text-[20px] font-extrabold text-zinc-800">{successLoop.totalClients}</p>
                    <p className="text-[10px] text-zinc-500 font-medium">{successLoop.totalClients === 1 ? 'klient' : 'klientów'}</p>
                  </div>
                  {successLoop.totalValue > 0 && (
                    <div className="text-center px-4 py-2 rounded-xl bg-zinc-50">
                      <p className="text-[20px] font-extrabold text-zinc-800">{successLoop.totalValue.toLocaleString('pl-PL')}<span className="text-[13px] ml-0.5">zł</span></p>
                      <p className="text-[10px] text-zinc-500 font-medium">zarobione</p>
                    </div>
                  )}
                </motion.div>

                {/* Flow visualization */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.64 }}
                  className="flex items-center justify-center gap-2 mb-6 text-[11px] font-semibold"
                >
                  <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Publikacja</span>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  <span className="px-2.5 py-1 rounded-full bg-[#F47A3B]/10 text-[#F47A3B]">Lead</span>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500 text-white">Klient</span>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  <span className="px-2.5 py-1 rounded-full bg-[#F47A3B] text-white animate-pulse">Publikuj!</span>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.72 }}
                  className="space-y-2"
                >
                  <Button
                    onClick={() => { setSuccessLoop(null); navigate('/panel/promocja'); }}
                    className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl py-3.5 text-[15px] font-bold shadow-lg shadow-[#F47A3B]/25"
                    data-testid="success-loop-publish-btn"
                  >
                    <Rocket className="w-5 h-5 mr-2" />
                    Dodaj kolejną publikację
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setSuccessLoop(null); setActiveFilter('closed'); }}
                    className="w-full rounded-xl py-3 text-[13px] font-semibold border-zinc-200"
                    data-testid="success-loop-clients-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Zobacz klientów
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Response Hub Drawer */}
      <ResponseHubDrawer
        lead={responseHubLead}
        isOpen={!!responseHubLead}
        onClose={() => setResponseHubLead(null)}
        onStatusChange={(leadId, status) => {
          updateStatus(leadId, status);
          setResponseHubLead(null);
        }}
      />
    </StudioShell>
  );
}
